import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'usehooks-ts';
import type { ActionPillConfig } from '../../components/action-pills';
import type { SuggestionItem } from '../../components/suggestion-list';
import { buildHomeSuggestions } from '../../data/home-suggestions';
import type { AskPercyVariant } from '../use-pills';

// Long enough to read a headline before the next one waves in.
const HEADLINE_HOLD_MS = 4500;
const ROW_SIZE = 3;
const ROWS_SHOWN = 2;
const ROWS_EXPANDED = 4;
// Character widths fitted to the labels as rendered (label-3 Inter). Every pill has the same icon
// and padding, so only the label tells rows apart.
const NARROW_CHARS = new Set([...'fijlrtI ']);
const WIDE_CHARS = new Set([...'mwMW']);

type Suggestion = SuggestionItem & {
  headline?: string;
  /** Previewed in the composer's placeholder while the suggestion is hovered. */
  prompt: string;
  select: () => void;
};

type UseSuggestionsParams = {
  variant?: AskPercyVariant;
  pills: ActionPillConfig[] | null;
  /** Only the centred page lists suggestions; the docked panel shows the pills as ghost rows. */
  listed: boolean;
  /** A phone swipes one row of suggestions, so it gets all of them at once instead of Show more. */
  showAll: boolean;
  cycleHeadline: boolean;
  seedInput: SeedInput;
};

type SeedInput = (prompt: string, options?: { sandbox?: boolean }) => void;

const homeSuggestions = (t: TFunction<'ask-percy'>, seedInput: SeedInput): Suggestion[] =>
  buildHomeSuggestions(t).map(({ sandbox, ...suggestion }) => ({
    ...suggestion,
    select: () => seedInput(suggestion.prompt, { sandbox }),
  }));

// Outside the home, the variant's pills are listed with their template menus opened out.
const pillSuggestions = (pills: ActionPillConfig[]): Suggestion[] =>
  pills.flatMap<Suggestion>(pill =>
    pill.kind === 'action'
      ? [
          {
            id: pill.id,
            label: pill.label,
            icon: pill.icon,
            prompt: pill.prompt,
            select: () => pill.onSelect(pill.prompt),
          },
        ]
      : pill.templates.map(template => ({
          id: `${pill.id}:${template.id}`,
          label: template.title,
          icon: template.icon,
          prompt: template.prompt,
          select: () => pill.onSelect(template),
        })),
  );

const shuffle = <T>(items: T[]): T[] => {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
};

const charWidth = (char: string) => {
  if (NARROW_CHARS.has(char)) return 4.1;
  if (WIDE_CHARS.has(char)) return 9.3;
  return char === char.toUpperCase() && char !== char.toLowerCase() ? 8.5 : 7.7;
};

const estimateWidth = (suggestion: Suggestion) =>
  Array.from(suggestion.label).reduce((sum, char) => sum + charWidth(char), 0);

// Of every way to deal the suggestions into rows of three, the one whose rows come out closest in
// width, so the rows line up while each pill keeps its own size.
const dealEvenRows = (items: Suggestion[]): string[][] => {
  const widths = items.map(estimateWidth);
  let best: number[][] = [];
  let bestSpread = Number.POSITIVE_INFINITY;
  const deal = (remaining: number[], rows: number[][], narrowest: number, widest: number) => {
    if (widest - narrowest >= bestSpread) return;
    if (remaining.length === 0) {
      bestSpread = widest - narrowest;
      best = rows;
      return;
    }
    // The first remaining suggestion always opens the next row, so each split is tried once.
    const [first, ...rest] = remaining;
    for (let i = 0; i < rest.length; i += 1) {
      for (let j = i + 1; j < rest.length; j += 1) {
        const width = widths[first] + widths[rest[i]] + widths[rest[j]];
        deal(
          rest.filter((_, k) => k !== i && k !== j),
          [...rows, [first, rest[i], rest[j]]],
          Math.min(narrowest, width),
          Math.max(widest, width),
        );
      }
    }
  };
  deal(
    items.map((_, index) => index),
    [],
    Number.POSITIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
  );
  return best.map(row => row.map(index => items[index].id));
};

/**
 * The centred page's suggestions, and the home's headline cycle through the ones on screen: it
 * starts on the greeting and holds wherever it is once the user picks a suggestion or starts typing.
 * Each visit deals the home a random twelve of its set: two rows up front, two behind Show more.
 */
const useSuggestions = ({ variant, pills, listed, showAll, cycleHeadline, seedInput }: UseSuggestionsParams) => {
  const { t } = useTranslation('ask-percy');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [headlineIndex, setHeadlineIndex] = useState<number | null>(null);
  const [headlineHeld, setHeadlineHeld] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const home = variant === 'full' ? homeSuggestions(t, seedInput) : [];
  const [homeRows] = useState(() => {
    const dealt = Math.floor(Math.min(home.length, ROW_SIZE * ROWS_EXPANDED) / ROW_SIZE) * ROW_SIZE;
    return dealEvenRows(shuffle(home).slice(0, dealt));
  });
  const homeShown = homeRows
    .slice(0, expanded || showAll ? ROWS_EXPANDED : ROWS_SHOWN)
    .flat()
    .flatMap(id => home.find(suggestion => suggestion.id === id) ?? []);
  const suggestions: Suggestion[] = !listed ? [] : variant === 'full' ? homeShown : pillSuggestions(pills ?? []);
  const canShowMore = listed && variant === 'full' && !showAll && homeRows.length > ROWS_SHOWN;

  useInterval(
    () => setHeadlineIndex(index => (index === null ? 0 : (index + 1) % suggestions.length)),
    cycleHeadline && !headlineHeld && suggestions.length > 0 ? HEADLINE_HOLD_MS : null,
  );

  const select = (id: string) => {
    const suggestion = suggestions.find(candidate => candidate.id === id);
    if (!suggestion) return;
    setHeadlineHeld(true);
    setHoveredId(null);
    suggestion.select();
  };

  return {
    suggestions,
    headline: headlineIndex === null ? undefined : suggestions[headlineIndex % suggestions.length]?.headline,
    hoveredPrompt: suggestions.find(suggestion => suggestion.id === hoveredId)?.prompt,
    setHoveredId,
    select,
    holdHeadline: () => setHeadlineHeld(true),
    more: canShowMore ? { expanded, toggle: () => setExpanded(current => !current) } : undefined,
  };
};

export default useSuggestions;
